import React from "react";
import { Button, ColorPicker, Input, InputNumber, Radio, Select, Typography } from "@arco-design/web-react";
import { IconFolder } from "@arco-design/web-react/icon";
import { POPUP_PHONE_CHOICES, POPUP_PHONE_SECONDS, POPUP_QUIET_CHOICES, safeImageUrl } from "./formHtml";
import ImagePicker from "./ImagePicker";

const { Text } = Typography;

// Edits the builder form's content (image, headline, subheadline, button text,
// fine print) and style (layout, button shape/width, accent color). These are
// the pieces worth A/B testing: clone a form, change one of them, run both.

const labelStyle = { fontSize: 12, color: "var(--color-text-3)", marginBottom: 4 };

function Row({ label, hint, children }) {
    return (
        <div style={{ marginBottom: 12 }}>
            <div style={labelStyle}>{label}</div>
            {children}
            {hint && <Text type="secondary" style={{ fontSize: 12 }}>{hint}</Text>}
        </div>
    );
}

export function FormContentEditor({ content, setContent }) {
    const [picking, setPicking] = React.useState(false);
    const set = (patch) => setContent({ ...content, ...patch });
    const imageUrl = safeImageUrl(content.image_url);
    const imageInvalid = !!content.image_url && !imageUrl;
    // image_file names the Files asset the URL was published from, so the editor
    // can say where the image came from; typing a URL by hand clears it.
    const fromFiles = !!content.image_file && !!imageUrl;
    return (
        <div>
            <Row
                label="Image"
                hint={imageInvalid
                    ? "Must be a full http(s):// link to an image — it won't show otherwise."
                    : fromFiles ? `From Files: ${content.image_file}` : "Paste a link to an image, or choose one from Files."}
            >
                <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                    {imageUrl && (
                        <img src={imageUrl} alt="" style={{ width: 56, height: 42, objectFit: "cover", borderRadius: 4, flex: "none", border: "1px solid var(--color-border-2)" }} />
                    )}
                    <Input
                        value={content.image_url}
                        status={imageInvalid ? "error" : undefined}
                        placeholder="https://…/lone-star.jpg"
                        onChange={(v) => set({ image_url: v, image_file: "" })}
                        allowClear
                        style={{ flex: 1 }}
                    />
                    <Button icon={<IconFolder />} onClick={() => setPicking(true)}>From Files</Button>
                </div>
                <ImagePicker
                    visible={picking}
                    onCancel={() => setPicking(false)}
                    onPick={({ url, fileName }) => { set({ image_url: url, image_file: fileName }); setPicking(false); }}
                />
            </Row>
            <Row label="Headline">
                <Input value={content.headline} placeholder="Get the Lone Star Block Tutorial PDF FREE!" onChange={(v) => set({ headline: v })} />
            </Row>
            <Row label="Subheadline">
                <Input.TextArea
                    value={content.subheadline}
                    autoSize={{ minRows: 2 }}
                    placeholder="Subscribe to my newsletter and get my latest tutorial PDF for FREE."
                    onChange={(v) => set({ subheadline: v })}
                />
            </Row>
            <Row label="Button text">
                <Input value={content.cta_text} placeholder="Subscribe" onChange={(v) => set({ cta_text: v })} />
            </Row>
            <Row label="Fine print (below the form)">
                <Input.TextArea
                    value={content.fine_print}
                    autoSize={{ minRows: 2 }}
                    placeholder="By completing this form, I agree to receive emails…"
                    onChange={(v) => set({ fine_print: v })}
                />
            </Row>
            <div style={{ fontWeight: 600, margin: "16px 0 8px", color: "var(--color-text-1)" }}>After signing up</div>
            <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 8 }}>
                Shown in place of the form once someone subscribes, and whenever they come back to it. (If the campaign
                has a thank-you URL, they're sent there right after subscribing instead.)
            </Text>
            <Row label="Thank-you headline">
                <Input value={content.thanks_headline} placeholder="Thanks for signing up!" onChange={(v) => set({ thanks_headline: v })} />
            </Row>
            <Row label="Thank-you message">
                <Input.TextArea
                    value={content.thanks_text}
                    autoSize={{ minRows: 2 }}
                    placeholder="Check your inbox — your PDF is on its way."
                    onChange={(v) => set({ thanks_text: v })}
                />
            </Row>
        </div>
    );
}

export function FormStyleEditor({ style, setStyle }) {
    const set = (patch) => setStyle({ ...style, ...patch });
    return (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 24px" }}>
            <Row label="Layout">
                <Radio.Group
                    type="button"
                    size="small"
                    value={style.layout}
                    onChange={(v) => set({ layout: v })}
                    options={[
                        { label: "Card", value: "card" },
                        { label: "Image beside", value: "split" },
                        { label: "Minimal", value: "minimal" },
                    ]}
                />
            </Row>
            <Row label="Button shape">
                <Radio.Group
                    type="button"
                    size="small"
                    value={style.button_shape}
                    onChange={(v) => set({ button_shape: v })}
                    options={[
                        { label: "Square", value: "square" },
                        { label: "Rounded", value: "rounded" },
                        { label: "Pill", value: "pill" },
                    ]}
                />
            </Row>
            <Row label="Button width">
                <Radio.Group
                    type="button"
                    size="small"
                    value={style.button_width}
                    onChange={(v) => set({ button_width: v })}
                    options={[
                        { label: "Full", value: "full" },
                        { label: "Fit text", value: "auto" },
                    ]}
                />
            </Row>
            <Row label="Accent color">
                <ColorPicker
                    size="small"
                    showText
                    disabledAlpha
                    format="hex"
                    value={style.accent_color}
                    onChange={(v) => set({ accent_color: v })}
                />
            </Row>
        </div>
    );
}

// Where the form shows: where the snippet is pasted, or in a popup that opens
// when the visitor's mouse leaves the page.
// What each phone choice does, under the choice.
const PHONE_HINTS = {
    off: "Phones have no mouse, so the exit check can't run there. Phone visitors won't see the form.",
    scroll_up: "Opens when someone who has scrolled down flicks back up quickly, as people do to reach the address bar.",
    timer: "Opens once they've been on the page this long. Google ranks phone pages lower when a popup covers them right away, so don't make it too short.",
};

export function FormDisplayEditor({ display, setDisplay }) {
    const set = (patch) => setDisplay({ ...display, ...patch });
    const popup = display.mode === "popup";
    return (
        <div>
            <Row
                label="Show the form"
                hint={popup
                    ? "Opens over the page when the mouse heads for the tabs or address bar, once per page. It doesn't open for someone who already signed up."
                    : "Sits on the page where you paste the embed code."}
            >
                <Radio.Group
                    type="button"
                    size="small"
                    value={popup ? "popup" : "inline"}
                    onChange={(v) => set({ mode: v })}
                    options={[
                        { label: "On the page", value: "inline" },
                        { label: "Exit popup", value: "popup" },
                    ]}
                />
            </Row>
            {popup && (
                <>
                    <Row
                        label="Opens when the mouse leaves through"
                        hint={display.trigger === "any"
                            ? "Any edge also catches people reaching for the scrollbar, another window or the Dock, so it opens more often."
                            : "The top is where people go to close the tab, type a new address or go back."}
                    >
                        <Radio.Group
                            type="button"
                            size="small"
                            value={display.trigger === "any" ? "any" : "top"}
                            onChange={(v) => set({ trigger: v })}
                            options={[
                                { label: "Top only", value: "top" },
                                { label: "Any edge", value: "any" },
                            ]}
                        />
                    </Row>
                    <Row
                        label="On phones and tablets"
                        hint={PHONE_HINTS[display.phone] || PHONE_HINTS.off}
                    >
                        <Select
                            size="small"
                            value={display.phone || "off"}
                            onChange={(v) => set({ phone: v })}
                            options={POPUP_PHONE_CHOICES}
                            style={{ width: 200 }}
                        />
                        {display.phone === "timer" && (
                            <span style={{ marginLeft: 8, whiteSpace: "nowrap" }}>
                                <InputNumber
                                    size="small"
                                    min={POPUP_PHONE_SECONDS.min}
                                    max={POPUP_PHONE_SECONDS.max}
                                    step={5}
                                    precision={0}
                                    value={display.phone_seconds}
                                    onChange={(v) => v && set({ phone_seconds: v })}
                                    style={{ width: 90 }}
                                    aria-label="Seconds on the page"
                                />
                                <span style={{ marginLeft: 6 }}>seconds on the page</span>
                            </span>
                        )}
                    </Row>
                    <Row label="After someone closes it, it can open again">
                        <Select
                            size="small"
                            value={display.quiet_hours}
                            onChange={(v) => set({ quiet_hours: v })}
                            options={POPUP_QUIET_CHOICES}
                            style={{ width: 200 }}
                        />
                    </Row>
                </>
            )}
        </div>
    );
}

// The preview grows with the form up to this height, then scrolls.
const PREVIEW_MAX_HEIGHT = 1200;

// Renders the generated form HTML in a sandboxed iframe (scripts off, so the
// preview can't submit), sized to its content. popup shows it as the popup
// looks: a box with a close button over a dimmed page.
export function FormPreview({ html, popup }) {
    const ref = React.useRef(null);
    const observer = React.useRef(null);
    const [height, setHeight] = React.useState(300);
    // display:flow-root keeps the form's own top and bottom margins inside the
    // body, so the body's height is the whole form.
    const body = popup
        ? `<body style="margin:0;padding:24px 12px;background:#6b6b6b;display:flow-root">` +
          `<div style="position:relative;max-width:640px;margin:0 auto;background:#fff;border-radius:10px;box-shadow:0 12px 40px rgba(0,0,0,.3);padding:32px 16px 16px;box-sizing:border-box">` +
          `<span style="position:absolute;top:6px;right:8px;width:32px;height:32px;font:400 28px/32px Helvetica,Arial,sans-serif;color:#666;text-align:center">&times;</span>` +
          `${html}</div></body>`
        : `<body style="margin:0;background:#fff;display:flow-root">${html}</body>`;
    const doc = `<!doctype html><html><head><meta name="viewport" content="width=device-width,initial-scale=1"></head>` +
        `${body}</html>`;
    // Fit the frame to the form; only a very tall form gets a scrollbar.
    const measure = () => {
        const body = ref.current && ref.current.contentDocument && ref.current.contentDocument.body;
        if (body) setHeight(Math.min(PREVIEW_MAX_HEIGHT, Math.max(120, Math.ceil(body.getBoundingClientRect().height) + 2)));
    };
    React.useEffect(() => () => observer.current && observer.current.disconnect(), []);
    return (
        <iframe
            ref={ref}
            title="Form preview"
            sandbox="allow-same-origin"
            srcDoc={doc}
            onLoad={() => {
                measure();
                // The form grows after load as images and fonts arrive, and
                // reflows when the pane is resized; re-measure each time.
                const body = ref.current?.contentDocument?.body;
                if (observer.current) observer.current.disconnect();
                if (body && typeof ResizeObserver !== "undefined") {
                    // Resize on the next frame: changing the frame's height
                    // inside the callback makes the browser report a loop.
                    observer.current = new ResizeObserver(() => window.requestAnimationFrame(measure));
                    observer.current.observe(body);
                }
                const imgs = ref.current?.contentDocument?.images || [];
                Array.from(imgs).forEach((img) => img.addEventListener("load", measure));
            }}
            style={{ width: "100%", height, border: "1px solid var(--color-border-2)", borderRadius: 6, background: "#fff" }}
        />
    );
}
